// Offscreen-canvas waveform renderer for the Konva timeline.
//
// Renders peak data into a `<canvas>` (not attached to the DOM) which can be
// passed straight to a Konva.Image. We deliberately render only a slice of the
// full media -- the visible viewport plus a buffer -- so the canvas size stays
// bounded even at extreme zoom levels.

/**
 * Render a waveform tile into an offscreen canvas.
 *
 * @param {object} options
 * @param {number[][]} options.peaks         - normalized peak data (channel 0 used)
 * @param {number} options.pxPerSec          - current zoom level
 * @param {number} options.durationSec       - total media duration
 * @param {number} options.startSec          - tile start time (clamped to >= 0)
 * @param {number} options.endSec            - tile end time (clamped to <= duration)
 * @param {number} options.height            - canvas height
 * @param {string} [options.waveColor]
 * @returns {HTMLCanvasElement|null}
 */
export function renderWaveformTile({
    peaks,
    pxPerSec,
    durationSec,
    startSec,
    endSec,
    height,
    waveColor = "#0fbc8c"
} = {}) {
    if (!peaks || !pxPerSec || !height || typeof document === "undefined") return null;
    const safeDuration = Math.max(0, durationSec || 0);
    const s = Math.max(0, Math.min(startSec || 0, safeDuration || startSec || 0));
    const e = Math.max(s, safeDuration ? Math.min(endSec || 0, safeDuration) : endSec || 0);
    const widthSec = e - s;
    if (widthSec <= 0) return null;

    const width = Math.max(1, Math.ceil(widthSec * pxPerSec));
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;

    const channel = peaks[0] || [];
    if (channel.length === 0 || !safeDuration) {
        // Flat baseline render.
        ctx.fillStyle = waveColor;
        ctx.fillRect(0, Math.floor(height / 2) - 1, width, 2);
        return canvas;
    }

    // peaks span the full duration; map time -> sample index.
    const samplesPerSec = channel.length / safeDuration;
    const startSample = Math.max(0, Math.floor(s * samplesPerSec));
    const endSample = Math.min(channel.length, Math.ceil(e * samplesPerSec));
    const sliceLen = endSample - startSample;
    if (sliceLen <= 0) {
        ctx.fillStyle = waveColor;
        ctx.fillRect(0, Math.floor(height / 2) - 1, width, 2);
        return canvas;
    }

    ctx.fillStyle = waveColor;
    const halfH = height / 2;
    // For each px column, find the peak amplitude in its sample range.
    const samplesPerPx = sliceLen / width;
    if (samplesPerPx >= 1) {
        for (let px = 0; px < width; px += 1) {
            const sampleStart = startSample + Math.floor(px * samplesPerPx);
            const sampleEnd = Math.max(sampleStart + 1, startSample + Math.floor((px + 1) * samplesPerPx));
            let max = 0;
            for (let i = sampleStart; i < sampleEnd && i < channel.length; i += 1) {
                const v = channel[i];
                const abs = v < 0 ? -v : v;
                if (abs > max) max = abs;
            }
            if (max <= 0) max = 0.005; // keep a hairline so silence is visible
            const barHeight = Math.max(1, Math.round(max * height));
            const y = Math.floor(halfH - barHeight / 2);
            ctx.fillRect(px, y, 1, barHeight);
        }
    } else {
        // Zoomed in so far that each sample maps to multiple px. Linear-interp
        // the amplitude across them so the wave stays smooth.
        const pxPerSample = width / sliceLen;
        for (let i = 0; i < sliceLen; i += 1) {
            const sampleIdx = startSample + i;
            const v = channel[sampleIdx];
            const abs = v < 0 ? -v : v;
            const barHeight = Math.max(1, Math.round(abs * height));
            const y = Math.floor(halfH - barHeight / 2);
            const x = Math.floor(i * pxPerSample);
            const w = Math.max(1, Math.ceil(pxPerSample));
            ctx.fillRect(x, y, w, barHeight);
        }
    }

    return canvas;
}
